<template>
  <div role="dialog" aria-modal="true" class="popup fadein absolute z-50 flex justify-center items-center">
    <div class="popup-wrapper z-50 xl:w-1/3 md:w-3/4 w-full max-w-lg max-h-4/6 overflow-scroll flex flex-row-reverse rounded-xl bg-slate-middle">
      <div
        tabindex="0"
        role="button"
        ref="firstFocus"
        class="absolute h-6 w-6 mr-2 mt-3 p-1.5 bg-black rounded-full flex justify-center items-center cursor-pointer"
        @pointerdown="close()"
        @keydown.enter="close()"
      >
        <img class="w-5 h-5 invert rotate-45" :src="Close" />
      </div>
      <div v-if="user" class="flex flex-col items-start w-full p-9">
        <!-- PIN -->
        <div class="flex items-center justify-between w-full text-slate-white">
          <p class="text-lg">{{ t('shareWithPIN') }}</p>
          <p
            v-if="props.wall.details.pin.length > 0"
            class="gray-btn cursor-pointer p-3 px-5 text-sm mr-1"
            @click="copyToClipBoard(props.wall.details.pin)"
            @touchstart.prevent="copyToClipBoard(props.wall.details.pin)"
          >
            {{ props.wall.details.pin }}
          </p>
          <div v-if="props.userIsOwner" class="bg-slate-middle p-5 pr-0 mt-2 rounded-r-lg">
            <Toggle :value="props.wall.details.pin.length > 0" colour="green" @toggle-value="togglePINControl()" />
          </div>
        </div>

        <!-- Permalink -->
        <div v-if="props.userIsOwner" class="flex justify-between items-center w-full text-slate-white">
          <div class="flex items-center">
            <p class="text-lg">{{ t('shareWithLink') }}</p>
          </div>
          <p
            v-if="props.wall.status.permaLink"
            class="rounded-3xl cursor-pointer p-3 px-5 text-sm bg-blue-middle"
            @click="copyToClipBoard(assembledPermaLink)"
          >
            {{ t('copyLink') }}
          </p>
          <div class="bg-slate-middle p-5 pr-0 mt-2 rounded-r-lg">
            <Toggle :value="props.wall.status.permaLink" colour="green" @toggle-value="togglePermaLinkControl()" />
          </div>
        </div>

        <div v-if="props.userIsOwner" class="h-px bg-slate-bright w-full my-4"></div>
        <div v-else class="mt-3">
          <Notice :type="DialogMessageType.Warning" :text="t('notowner')" />
        </div>

        <!-- Sharing -->
        <div v-if="props.userIsOwner && user.profile.provider === PlatformType.DATAPORTEN" class="flex flex-col items-start w-full">
          <p class="text-slate-white text-lg">{{ t('feideHeadline') }}</p>
          <div class="flex w-full cursor-pointer mt-3 bg-slate-dark rounded-lg" @click="setgroupDropdownIsVisible()">
            <input
              v-model="state.selectedFeideGroup"
              disabled
              class="flex-grow text-slate-bright bg-slate-dark text-md p-3 rounded-lg resize-none overflow-scroll"
              :placeholder="state.selectedFeideGroup"
            />
            <div class="w-10 flex flex-row justify-center items-center">
              <img class="-rotate-90 w-3 invert" :src="Arrow" />
            </div>
          </div>

          <div v-if="state.groupDropdownIsVisible" class="bg-slate-dark -mt-4 p-3 w-full rounded-b-lg max-h-56 overflow-scroll">
            <div
              v-for="(group, index) in feideGroups"
              :key="index"
              class="flex justify-between items-center cursor-pointer text-left py-px my-2"
              @click="selectGroup(group)"
            >
              <p class="text-md text-slate-white">{{ group.name }}</p>
              <div v-if="sharedGroups.findIndex((o) => o._id === group._id) !== -1" class="flex items-center">
                <p class="text-slate-bright text-sm">{{ t('alreadyshared') }}</p>
              </div>
            </div>
          </div>

          <div v-if="userIsUiOUser" class="flex flex-col items-start w-full mt-3">
            <p class="text-slate-white text-lg">{{ t('canvasHeadline') }}</p>
            <div
              class="flex w-full cursor-pointer mt-3 bg-slate-dark rounded-lg"
              @click="state.canvasDropdownIsVisible = !state.canvasDropdownIsVisible"
            >
              <input
                v-model="state.selectedCanvasGroup"
                disabled
                class="flex-grow text-slate-bright bg-slate-dark text-md p-3 rounded-lg resize-none overflow-scroll"
                :placeholder="state.selectedCanvasGroup"
              />
              <div class="w-10 flex flex-row justify-center items-center">
                <img class="-rotate-90 w-3 invert" :src="Arrow" />
              </div>
            </div>
            <div v-if="state.canvasDropdownIsVisible" class="bg-slate-dark -mt-4 p-3 w-full rounded-b-lg max-h-56 overflow-scroll">
              <div
                v-for="(group, index) in canvasGroups"
                :key="index"
                class="flex justify-between items-center cursor-pointer text-left py-px my-2"
                @click="selectGroup(group)"
                @touchstart.prevent="selectGroup(group)"
              >
                <p class="text-md text-slate-white">{{ group.name }}</p>
                <div v-if="sharedGroups.findIndex((o) => o._id === group._id) !== -1" class="flex items-center">
                  <p class="text-slate-bright text-sm">{{ t('alreadyshared') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="state.isLoading" class="w-full flex justify-center py-7">
            <Loader />
          </div>

          <div v-else class="flex items-start py-5 flex-col overflow-visible w-full">
            <div v-for="(group, index) in sharedGroups" :key="index" class="flex items-center justify-between w-full my-1.5">
              <div class="flex">
                <div
                  class="w-9 h-9 rounded-full mr-3 text-white flex justify-center items-center cursor-default"
                  :style="`background-color: ${color.actions.selectColor(group.name).background};`"
                >
                  <span :style="`color: ${color.actions.selectColor(group.name).text};`">{{ shortenedGroupName(group.name) }}</span>
                </div>
                <div class="relative inline-block text-left" @click="setPermissionDropdownIsVisible(index, group.platform)">
                  <p class="text-left text-lg text-white cursor-default">
                    {{ group.name.substring(0, 14) }} {{ group.name.length > 14 ? '...' : '' }}
                  </p>
                  <div class="flex cursor-pointer">
                    <p class="text-left text-md text-slate-white capitalize">{{ getAttributeForGroup(group.originalId, 'permissionLevel') }}</p>
                    <img class="-rotate-90 w-3 ml-1.5 invert" :src="Arrow" />
                  </div>
                  <!-- <div
                    v-if="state.permissionDropdownIsVisible[index]"
                    class="text-slate-white text-md absolute ml-11 rounded-xl bg-slate-dark px-5 py-2 z-50"
                  >  -->
                  <div
                    v-if="state.permissionDropdownIsVisible[index]"
                    class="absolute left-0 -top-3 min-w-[12rem] rounded-xl bg-slate-dark px-5 py-2 text-slate-white text-md shadow-lg z-50"
                  >
                    <p
                      v-if="getAttributeForGroup(group.originalId, 'permissionLevel') !== PermissionLevel.Organize"
                      class="my-1.5 text-left cursor-pointer"
                      @click="changePermissionLevel(PermissionLevel.Organize, group)"
                    >
                      {{ t('organizeTag') }}
                    </p>
                    <p
                      v-if="getAttributeForGroup(group.originalId, 'permissionLevel') !== PermissionLevel.Join"
                      class="my-1.5 text-left cursor-pointer"
                      @click="changePermissionLevel(PermissionLevel.Join, group)"
                    >
                      {{ t('joinTag') }}
                    </p>
                    <p
                      v-if="getAttributeForGroup(group.originalId, 'permissionLevel') !== PermissionLevel.Copy"
                      class="my-1.5 text-left cursor-pointer"
                      @click="changePermissionLevel(PermissionLevel.Copy, group)"
                    >
                      {{ t('copyTag') }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex">
                <div class="text-slate-white bg-slate-dark p-1 px-3 mb-px rounded-2xl text-sm lowercase cursor-default">
                  <span>{{ getAttributeForGroup(group.originalId, 'groupType') }}</span>
                </div>
                <div class="flex">
                  <div
                    v-if="sharedGroups.findIndex((o) => o._id === group._id) !== -1"
                    class="red-btn rounded-full z-50 text-white w-8 h-8 mx-2 flex justify-center items-center cursor-pointer"
                    @click="submitGroupRemoval(group)"
                    @touchstart.prevent="submitGroupRemoval(group)"
                  >
                    <img class="w-3 rotate-45 invert" :src="Plus" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Canvas / LTI: prompt to log in with Feide for group sharing -->
        <div
          v-else-if="props.userIsOwner && user.profile.provider === PlatformType.CANVAS"
          class="flex flex-col items-start w-full"
        >
          <div
            class="flex w-full cursor-pointer mt-1 bg-slate-dark rounded-lg p-3"
            role="link"
            tabindex="0"
            @click="loginWithFeide()"
            @keydown.enter="loginWithFeide()"
          >
            <p class="text-md text-slate-white text-left">{{ t('canvasLoginHint') }}</p>
          </div>
        </div>

        <!-- Lock wall -->
        <div v-if="props.userIsOwner" class="flex justify-between items-center w-full text-slate-white">
          <div class="flex items-center">
            <p class="text-lg">{{ t('visibility') }}</p>
          </div>
          <Toggle
            :value="props.wall.status.readOnly"
            colour="red"
            @toggle-value="lockWall()"
            :message="{ on: `${t('board')} ${t('locked')}`, off: `${t('board')} ${t('unlocked')}` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { reactive, onMounted, PropType, computed, ref, nextTick } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { Sharing, Wall, ISharing, DialogMessageType } from '@/common/classes'
  import { StoreResponse, Group, PlatformType } from '@/common/interfaces'
  import { authMethods, GroupType, LoginType, ParcelType, PermissionLevel } from '@/constants'

  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useCommonStore from '@/composables/common'
  import useColorStore from '@/composables/colors'
  import useDialogStore from '@/composables/dialog'

  import Loader from '@/components/common/Loader.vue'
  import Notice from '@/components/dialog/Notice.vue'
  import Toggle from '@/components/common/Toggle.vue'

  import Close from './../../assets/icons/fontawesome/plus-solid.svg'
  import Plus from './../../assets/icons/fontawesome/plus-solid.svg'
  import Arrow from './../../assets/icons/fontawesome/angle-left-solid.svg'

  const messages = {
    no: {
      shareWithPIN: 'Delta med PIN',
      shareWithLink: 'Delta med lenke',
      copyLink: 'Kopier lenke',
      feideHeadline: 'Del i Feide-gruppe ',
      canvasHeadline: 'Del i Canvas-emne',
      locked: 'låst',
      unlocked: 'ulåst',
      visibility: 'Tillate nye bidrag?',
      permalink: 'Permalenke',
      organizeTag: 'Kan organisere',
      joinTag: 'Kan delta',
      copyTag: 'Kan kopiere',
      alreadyshared: 'allerede delt',
      alreadysharedlong: 'Du har allerede delt med denne gruppen',
      dropdown: 'Velg en fra listen',
      showpin: 'Vis PIN',
      nousersingroup: 'Det er ingen brukere i gruppa',
      permalinkError: 'Could not change Permalink control state',
      canvasLoginHint: 'Logg inn med feide for å se gruppedeling',
    },
    en: {
      shareWithPIN: 'Share with PIN',
      shareWithLink: 'Share with lenke',
      copyLink: 'Copy link',
      feideHeadline: 'Share in Feide groups',
      canvasHeadline: 'Share in Canvas courses',
      locked: 'Locked',
      unlocked: 'Unlocked',
      visibility: 'Allow new contributions?',
      permalink: 'Permalink',
      organizeTag: 'Can organise',
      joinTag: 'Can join',
      copyTag: 'Can copy',
      alreadyshared: 'already shared',
      alreadysharedlong: 'You have already shared to this group',
      dropdown: 'Choose one from the list',
      showpin: 'Show PIN',
      nousersingroup: 'There are no users in the group',
      permalinkError: 'Could not change Permalink control state',
      canvasLoginHint: 'Log in with Feide to see group sharing options',
    },
  }

  const { t } = useI18n({ messages })

  const color = useColorStore()
  const common = useCommonStore()
  const wallStore = useWallStore()
  const userStore = useUserStore()
  const dialogStore = useDialogStore()

  const props = defineProps({
    wall: { type: Object as PropType<Wall>, required: true },
    userIsOwner: { type: Boolean, default: false },
  })

  const emit = defineEmits<{
    (e: 'closePopup'): void
  }>()

  const state = reactive({
    selectedFeideGroup: '',
    selectedCanvasGroup: '',
    groupDropdownIsVisible: false,
    canvasDropdownIsVisible: false,

    permissionDropdownIsVisible: [] as boolean[],
    linkDropdownIsVisible: false,

    selectedLinkPermission: PermissionLevel.Owner,
    isLoading: false,
  })

  const user = userStore.getters.user
  const firstFocus = ref()

  onMounted(async () => {
    const u = user.value
    if (u) {
      state.selectedCanvasGroup = t('dropdown')
      state.selectedFeideGroup = t('dropdown')
    }
    await nextTick()
    firstFocus.value?.focus()
  })

  const sharedGroups = computed(() => {
    const groups = []
    const shares = props.wall.users.shares
    if (shares && user.value) {
      for (let share of shares) {
        let sharedGroup = user.value.profile.groups.find((g: Group) => g.originalId === share.group_id)
        if (sharedGroup) groups.push(sharedGroup)
      }
    }
    return groups
  })
  const feideGroups = computed(() => {
    return user.value ? user.value.profile.groups.filter((group: Group) => group.platform === PlatformType.DATAPORTEN) : []
  })

  const canvasGroups = computed(() => {
    return user.value ? user.value.profile.groups.filter((group: Group) => group.platform === PlatformType.CANVAS) : []
  })

  const assembledPermaLink = computed(() => {
    const port = import.meta.env.VITE_SERVER_HOST.includes('localhost') ? `:${import.meta.env.VITE_CLIENT_PORT}` : ''
    return `${import.meta.env.VITE_SERVER_HOST}${port}/login?link=${props.wall.details.permaLink}`
  })

  async function deletePinForWall() {
    const updatedWall = new Wall(props.wall)
    updatedWall.details.pin = ''
    await wallStore.actions.deletePin(updatedWall)
  }

  function copyToClipBoard(text: string) {
    common.actions.copyTextToClipboard(text)
    dialogStore.actions.pushMessage('', DialogMessageType.Information, t('copiedtoclipboard'))
  }

  async function togglePermaLinkControl() {
    wallStore.actions.togglePermaLinkControl(props.wall._id).then((response) => {
      if (!response.success) {
        dialogStore.actions.pushMessage(t('permalinkError'), DialogMessageType.Error)
      }
    })
  }

  async function togglePINControl() {
    if (props.wall.details.pin.length > 0) await deletePinForWall()
    else await wallStore.actions.createPin(props.wall)
  }

  const userIsUiOUser = computed(() => {
    return user.value ? user.value.profile.email.toLowerCase().includes('uio') : false
  })

  function shortenedGroupName(name: string) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  async function submitGroupRemoval(group: Group) {
    state.isLoading = true
    const sharing = props.wall.users.shares.find((s: Sharing) => s.group_id === group.originalId)
    if (sharing) {
      await wallStore.actions.deleteShare(props.wall._id, sharing._id)
      state.isLoading = false
    }
  }

  function getAttributeForGroup(id?: string, attributeName?: string) {
    const share = props.wall.users.shares.find((s: Sharing) => s.group_id === id)
    if (attributeName) {
      let attribute = attributeName as keyof typeof share
      if (share && attribute) return getAttributeTranslation(share[attribute], attributeName)
    }
  }

  function getAttributeTranslation(attribute: string | PermissionLevel | GroupType, type: string) {
    if (type === 'permissionLevel') return t(`${attribute}`)
    else return attribute
  }

  async function lockWall() {
    const lockedWall = new Wall(props.wall)
    lockedWall.status.readOnly = !lockedWall.status.readOnly
    wallStore.actions.updateWall(lockedWall).then((res) => {
      if (res.success) {
        dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('lockedwall'))
        if (window.location.href.includes('dashboard')) {
          close()
          wallStore.actions.getUserWalls('') // get all walls again to circumvent page refresh when modifying wall from dashboard
        }
      }
    })
  }

  async function selectGroup(group: Group) {
    if (group.platform === PlatformType.CANVAS) state.selectedCanvasGroup = group.name
    else state.selectedFeideGroup = group.name

    const index = sharedGroups.value.findIndex((o) => o.originalId === group.originalId)
    if (index === -1 && wallStore.getters.selectedWall.value) {
      const sharing = new Sharing({
        _id: '',
        created: new Date(),
        access: true,
        group_id: group.originalId,
        groupType: group.platform,
        permissionLevel: PermissionLevel.Join,
      } as unknown as ISharing)
      const res = await wallStore.actions.createShare(wallStore.getters.selectedWall.value._id, sharing)
      if (!res.success) dialogStore.actions.pushMessage('', DialogMessageType.Error, t('nousersingroup'), 5000)
    } else dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('alreadysharedlong'))
    closeDropdown()
  }

  function setPermissionDropdownIsVisible(index: number, platform: string) {
    state.permissionDropdownIsVisible[index] = state.permissionDropdownIsVisible === null ? true : !state.permissionDropdownIsVisible[index]
    for (let i = 0; i < state.permissionDropdownIsVisible.length; i++) {
      if (i !== index && platform == PlatformType.DATAPORTEN) state.permissionDropdownIsVisible[i] = false // hide all others
    }
  }

  async function changePermissionLevel(level: PermissionLevel, group: Group) {
    const existingSharing = props.wall.users.shares.find((s: Sharing) => s.group_id === group.originalId)
    if (existingSharing) {
      const sharing = new Sharing(existingSharing)
      sharing.permissionLevel = level
      await wallStore.actions.changeShare(props.wall._id, sharing, ParcelType.WallShared)
    }
  }

  function close() {
    emit('closePopup')
  }

  function loginWithFeide() {
    const feide = authMethods.find((method) => method.type === LoginType.Feide)
    if (feide?.url) window.location.href = feide.url
  }

  function setgroupDropdownIsVisible() {
    state.groupDropdownIsVisible = !state.groupDropdownIsVisible
  }

  function closeDropdown() {
    state.groupDropdownIsVisible = false
    state.canvasDropdownIsVisible = false
  }
</script>

<style scoped></style>
